import {useState} from 'react'
import {Pressable, ScrollView, StyleSheet, View} from 'react-native'

import {Text} from '#/components/Typography'

const LIVE_ROOMS = [
  {title: 'Creator Corner', host: 'Maya + 48 listening', accent: '#FF7A59'},
  {title: 'Tech after hours', host: 'Jordan + 31 listening', accent: '#5B7FFF'},
  {title: 'Music discovery', host: 'Amara + 22 listening', accent: '#9B59B6'},
]

const ROOMS = [
  {
    title: 'Building in public',
    description:
      'Share progress, lessons, launches, and honest founder stories.',
    members: '12.8K members',
    emoji: '🚀',
  },
  {
    title: 'Film & television',
    description:
      'Reviews, recommendations, premieres, and scene-by-scene talk.',
    members: '9.4K members',
    emoji: '🎬',
  },
  {
    title: 'Culture & community',
    description: 'A welcoming place for the conversations shaping culture now.',
    members: '8.1K members',
    emoji: '🌍',
  },
  {
    title: 'Design exchange',
    description:
      'Product critique, visual inspiration, resources, and feedback.',
    members: '6.7K members',
    emoji: '✦',
  },
]

export function RoomsScreen() {
  const [joined, setJoined] = useState<Record<string, boolean>>({})

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <View style={styles.hero}>
        <Text style={styles.eyebrow}>ECHOROOM COMMUNITIES</Text>
        <Text style={styles.title}>Find your room.</Text>
        <Text style={styles.subtitle}>
          Join conversations built around the people and interests you care
          about.
        </Text>
      </View>

      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Live Rooms</Text>
          <Text style={styles.sectionCaption}>Listen in right now</Text>
        </View>
        <View style={styles.livePill}>
          <View style={styles.liveDot} />
          <Text style={styles.liveText}>LIVE</Text>
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.liveRow}>
        {LIVE_ROOMS.map(room => (
          <Pressable key={room.title} style={styles.liveCard}>
            <View style={[styles.liveAvatar, {backgroundColor: room.accent}]}>
              <Text style={styles.liveAvatarText}>{room.title[0]}</Text>
            </View>
            <Text numberOfLines={2} style={styles.liveTitle}>
              {room.title}
            </Text>
            <Text numberOfLines={1} style={styles.meta}>
              {room.host}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Discover Rooms</Text>
          <Text style={styles.sectionCaption}>Communities picked for you</Text>
        </View>
      </View>

      <View style={styles.roomList}>
        {ROOMS.map(room => {
          const isJoined = joined[room.title]
          return (
            <View key={room.title} style={styles.roomCard}>
              <View style={styles.roomIcon}>
                <Text style={styles.roomEmoji}>{room.emoji}</Text>
              </View>
              <View style={styles.roomBody}>
                <Text style={styles.roomTitle}>{room.title}</Text>
                <Text style={styles.roomDescription}>{room.description}</Text>
                <Text style={styles.meta}>{room.members}</Text>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`${isJoined ? 'Leave' : 'Join'} ${room.title}`}
                onPress={() =>
                  setJoined(current => ({
                    ...current,
                    [room.title]: !current[room.title],
                  }))
                }
                style={[styles.joinButton, isJoined && styles.joinedButton]}>
                <Text style={[styles.joinText, isJoined && styles.joinedText]}>
                  {isJoined ? 'Joined' : 'Join'}
                </Text>
              </Pressable>
            </View>
          )
        })}
      </View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: '#FFFFFF'},
  content: {paddingBottom: 120},
  hero: {
    backgroundColor: '#001F15',
    paddingHorizontal: 22,
    paddingTop: 48,
    paddingBottom: 34,
  },
  eyebrow: {
    color: '#70C9A8',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.5,
    marginBottom: 12,
  },
  title: {color: '#FFFFFF', fontSize: 34, fontWeight: '800', letterSpacing: -1},
  subtitle: {
    color: '#CFE7DE',
    fontSize: 16,
    lineHeight: 23,
    marginTop: 8,
    maxWidth: 520,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingTop: 24,
    paddingBottom: 13,
  },
  sectionTitle: {fontSize: 20, fontWeight: '800', color: '#101612'},
  sectionCaption: {fontSize: 13, color: '#65706A', marginTop: 2},
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FFF0ED',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 999,
  },
  liveDot: {width: 7, height: 7, borderRadius: 4, backgroundColor: '#F04B37'},
  liveText: {fontSize: 10, fontWeight: '800', color: '#C93625'},
  liveRow: {paddingHorizontal: 18, gap: 12, paddingBottom: 8},
  liveCard: {
    width: 158,
    minHeight: 150,
    borderWidth: 1,
    borderColor: '#E5EAE7',
    borderRadius: 18,
    padding: 14,
    backgroundColor: '#FFFFFF',
  },
  liveAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    borderWidth: 3,
    borderColor: '#DDF4EA',
  },
  liveAvatarText: {color: '#FFFFFF', fontSize: 20, fontWeight: '800'},
  liveTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontWeight: '700',
    color: '#101612',
    marginBottom: 7,
  },
  meta: {fontSize: 12, color: '#65706A'},
  roomList: {paddingHorizontal: 18, gap: 12},
  roomCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderWidth: 1,
    borderColor: '#E5EAE7',
    borderRadius: 18,
    padding: 14,
    backgroundColor: '#FFFFFF',
  },
  roomIcon: {
    width: 46,
    height: 46,
    borderRadius: 14,
    backgroundColor: '#E7F5EF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  roomEmoji: {fontSize: 22},
  roomBody: {flex: 1, paddingRight: 10},
  roomTitle: {fontSize: 16, fontWeight: '800', color: '#101612'},
  roomDescription: {
    fontSize: 13,
    lineHeight: 18,
    color: '#48524D',
    marginVertical: 5,
  },
  joinButton: {
    backgroundColor: '#003B25',
    borderWidth: 1,
    borderColor: '#003B25',
    borderRadius: 999,
    paddingHorizontal: 15,
    paddingVertical: 8,
  },
  joinedButton: {backgroundColor: '#FFFFFF'},
  joinText: {color: '#FFFFFF', fontSize: 12, fontWeight: '800'},
  joinedText: {color: '#003B25'},
})
